import type { Message, Part, QuestionRequest, Session } from "@openanalyst-ai/sdk/v2/client"
import { appChatSessionTitle, appChatSessionTitlePrefix, appChatSessionTitleSeparator } from "@/plugins/app-chat"
import type { RuntimePluginApp } from "@/plugins/runtime"
import { ACCEPTED_VIDEO_TYPES } from "@/constants/file-picker"
import { attachRefusalMessage } from "@/components/prompt-input/attach-copy"
import { pathKey } from "@/utils/path-key"
import { sessionQuestionRequest } from "../session/composer/session-request-tree"

type TransferItemLike = {
  kind?: string
  getAsFile?: () => File | null
}

type TransferLike = {
  items?: ArrayLike<TransferItemLike>
  files?: ArrayLike<File>
}

type AppSidebarInspectedAttachment =
  | { kind: "video"; filename: string; mime: string }
  | { kind: "attachment"; attachment: { filename: string; mime: string; dataUrl: string } }

function appSidebarNormalizedMime(value: string) {
  return value.split(";", 1)[0]?.trim().toLowerCase() ?? ""
}

function appSidebarDataUrlMime(value: string) {
  const comma = value.indexOf(",")
  if (comma < 5 || value.slice(0, 5).toLowerCase() !== "data:") return undefined
  const mime = appSidebarNormalizedMime(value.slice(5, comma))
  return mime.includes("/") ? mime : undefined
}

export function appSidebarAcceptedFileTypes(types: string[]) {
  return types.filter((mime) => !ACCEPTED_VIDEO_TYPES.includes(mime))
}

export function appSidebarAttachmentRefusal(input: { filename: string; mime?: string }) {
  if (!input.mime?.startsWith("video/")) return undefined
  const description = attachRefusalMessage({ ok: false, reason: "wrong-surface", filename: input.filename })
  if (!description) return undefined
  return { title: "Video attachment unavailable", description }
}

export function appSidebarVideoBoundary<T extends { filename: string; mime: string; dataUrl?: string }>(attachments: T[]) {
  const inspected = attachments.map((attachment) => {
    const declaredMime = appSidebarNormalizedMime(attachment.mime)
    const dataUrl = attachment.dataUrl
    const actualMime = dataUrl ? appSidebarDataUrlMime(dataUrl) : undefined
    const kind =
      declaredMime.startsWith("video/") || actualMime?.startsWith("video/")
        ? "video"
        : dataUrl && (!actualMime || actualMime !== declaredMime)
          ? "invalid"
          : "attachment"
    return {
      attachment,
      refusalMime: actualMime?.startsWith("video/") ? actualMime : declaredMime,
      kind,
    }
  })
  const video = inspected.find((item) => item.kind === "video")
  return {
    attachments: inspected.flatMap((item) => (item.kind === "attachment" ? [item.attachment] : [])),
    refusal: video
      ? appSidebarAttachmentRefusal({ filename: video.attachment.filename, mime: video.refusalMime || "video/*" })
      : undefined,
  }
}

export function appSidebarPluginAttachments(input: unknown[]) {
  const inspected = input.flatMap<AppSidebarInspectedAttachment>((attachment) => {
    if (!attachment || typeof attachment !== "object" || Array.isArray(attachment)) return []
    try {
      const filenameValue = Reflect.get(attachment, "filename")
      const mimeValue = Reflect.get(attachment, "mime")
      const filename = typeof filenameValue === "string" ? filenameValue.trim() : ""
      const mime = typeof mimeValue === "string" ? appSidebarNormalizedMime(mimeValue) : ""
      if (!filename || !mime) return []
      if (mime.startsWith("video/")) return [{ kind: "video", filename, mime }]
      const dataUrlValue = Reflect.get(attachment, "dataUrl")
      const dataUrl = typeof dataUrlValue === "string" ? dataUrlValue.trim() : ""
      const actualMime = dataUrl ? appSidebarDataUrlMime(dataUrl) : undefined
      if (actualMime?.startsWith("video/")) return [{ kind: "video", filename, mime: actualMime }]
      if (!actualMime || actualMime !== mime) return []
      return [{ kind: "attachment", attachment: { filename, mime, dataUrl } }]
    } catch {
      return []
    }
  })
  const video = inspected.find((item) => item.kind === "video")
  return {
    attachments: inspected.flatMap((item) => (item.kind === "attachment" ? [item.attachment] : [])),
    refusal: video ? appSidebarAttachmentRefusal({ filename: video.filename, mime: video.mime }) : undefined,
  }
}

export type AppSidebarPluginThread = {
  session: Session
  sessionID: string
  displayTitle: string
  updatedAt: number
  createdAt: number
  active: boolean
}

export type AppSidebarScrollbar = {
  visible: boolean
  top: number
  height: number
}

export function appSidebarQuestionRequest(input: {
  session: Session[]
  question: Record<string, QuestionRequest[] | undefined>
  sessionID?: string
}) {
  return sessionQuestionRequest(input.session, input.question, input.sessionID)
}

export function appSidebarPluginSessionTitle(pluginTitle: string) {
  return appChatSessionTitlePrefix(pluginTitle)
}

export function appSidebarPluginFullSessionTitle(input: { pluginTitle: string; title?: string }) {
  return appChatSessionTitle(input)
}

function appSidebarCompactThreadTitle(value: string) {
  const title = value.replace(/\s+/g, " ").trim()
  if (title.length <= 80) return title
  return `${title.slice(0, 77).trimEnd()}...`
}

function appSidebarPartTitle(part: Part) {
  if (part.type === "text" && "text" in part && typeof part.text === "string" && !part.synthetic && !part.ignored) {
    return appSidebarCompactThreadTitle(part.text)
  }
  if (part.type === "file" && "filename" in part && typeof part.filename === "string") return part.filename
  return ""
}

export function appSidebarPluginThreadContextTitle(input: {
  sessionID: string
  messages?: Message[]
  parts?: Record<string, Part[] | undefined>
}) {
  const message = input.messages?.find((item) => item.role === "user")
  if (!message) return ""

  return (input.parts?.[message.id] ?? []).map(appSidebarPartTitle).filter(Boolean).join(", ")
}

export function appSidebarPluginThreadDisplayTitle(input: {
  pluginTitle: string
  sessionTitle?: string
  contextTitle?: string
}) {
  const pluginTitle = input.pluginTitle.trim() || "Plugin App"
  const prefix = appSidebarPluginSessionTitle(pluginTitle)
  const title = input.sessionTitle?.trim()
  const fallback = input.contextTitle || `${pluginTitle} app chat`
  if (!title || title === prefix) return fallback

  const prefixedTitle = `${prefix}${appChatSessionTitleSeparator}`
  if (!title.startsWith(prefixedTitle)) return title

  return title.slice(prefixedTitle.length).trim() || fallback
}

export function appSidebarPluginThreadOrigin(input: { sessionTitle?: string; apps: RuntimePluginApp[] }) {
  const title = input.sessionTitle?.replace(/\s+/g, " ").trim()
  if (!title) return

  return input.apps.find((app) => {
    const pluginTitle = app.title.trim()
    if (!pluginTitle) return false
    const appChatPrefix = appSidebarPluginSessionTitle(pluginTitle)
    return (
      title === appChatPrefix ||
      title.startsWith(`${appChatPrefix}${appChatSessionTitleSeparator}`) ||
      title.startsWith(`${pluginTitle}:`)
    )
  })
}

export function appSidebarPluginThreads(input: {
  sessions: Session[]
  directory: string
  pluginTitle: string
  activeSessionID?: string
  messages?: Record<string, Message[] | undefined>
  parts?: Record<string, Part[] | undefined>
}) {
  const directory = pathKey(input.directory)
  const title = appSidebarPluginSessionTitle(input.pluginTitle)
  const prefixedTitle = `${title}${appChatSessionTitleSeparator}`

  return input.sessions
    .filter((session) => pathKey(session.directory) === directory)
    .filter((session) => session.title === title || session.title?.startsWith(prefixedTitle))
    .filter((session) => !session.parentID)
    .filter((session) => !(session.time as Session["time"] & { archived?: number }).archived)
    .sort((a, b) => {
      if (a.id === input.activeSessionID) return -1
      if (b.id === input.activeSessionID) return 1
      return b.time.updated - a.time.updated
    })
    .map(
      (session): AppSidebarPluginThread => ({
        session,
        sessionID: session.id,
        displayTitle: appSidebarPluginThreadDisplayTitle({
          pluginTitle: input.pluginTitle,
          sessionTitle: session.title,
          contextTitle: appSidebarPluginThreadContextTitle({
            sessionID: session.id,
            messages: input.messages?.[session.id],
            parts: input.parts,
          }),
        }),
        updatedAt: session.time.updated,
        createdAt: session.time.created,
        active: session.id === input.activeSessionID,
      }),
    )
}

export type AppSidebarAutoOpenedPanel = {
  pluginID?: string
  directory?: string
}

export function appSidebarShouldAutoOpenPanel(input: {
  pluginID?: string
  directory?: string
  railOnly: boolean
  autoOpened?: AppSidebarAutoOpenedPanel
}) {
  if (!input.pluginID || !input.directory || input.railOnly) return false
  if (!input.autoOpened) return true
  return input.autoOpened.pluginID !== input.pluginID || pathKey(input.autoOpened.directory ?? "") !== pathKey(input.directory)
}

export function appSidebarScrollbarState(input: {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
}): AppSidebarScrollbar {
  if (input.scrollHeight <= input.clientHeight) return { visible: false, top: 0, height: 100 }

  const height = Math.max(18, Math.min(100, (input.clientHeight / input.scrollHeight) * 100))
  const top = Math.min(
    100 - height,
    Math.max(0, (input.scrollTop / (input.scrollHeight - input.clientHeight)) * (100 - height)),
  )

  return {
    visible: true,
    top: Number(top.toFixed(2)),
    height: Number(height.toFixed(2)),
  }
}

export function appSidebarTransferFiles(input: TransferLike | null | undefined) {
  if (!input) return []

  const itemFiles = Array.from(input.items ?? []).flatMap((item) => {
    if (item.kind !== "file") return []
    const file = item.getAsFile?.()
    return file ? [file] : []
  })
  if (itemFiles.length) return itemFiles

  return Array.from(input.files ?? [])
}

export function appSidebarShouldReadNativeClipboard(input: { fileCount: number; plainText: string }) {
  return input.fileCount === 0 && input.plainText.length === 0
}

export function appSidebarShouldSubmitFromKey(input: {
  key: string
  shiftKey?: boolean
  metaKey?: boolean
  ctrlKey?: boolean
  altKey?: boolean
  isComposing?: boolean
}) {
  if (input.key !== "Enter") return false
  if (input.isComposing) return false
  return !input.shiftKey && !input.metaKey && !input.ctrlKey && !input.altKey
}
